import type { ReactElement } from 'react'
import { createBrowserRouter, createMemoryRouter, Navigate, type RouteObject } from 'react-router'
import { AccessGate, CondoRoute, ContextEntry } from '@/access/AccessGate'
import { FirstSectionRedirect, RequireCapability } from '@/access/RequireCapability'
import { SECTIONS, type SectionId } from '@/access/sections'
import { PairingScreen } from '@/auth/PairingScreen'
import { RequireAuth } from '@/auth/RequireAuth'
import { ApprovalsView } from '@/features/approvals/ApprovalsView'
import { BalancesView } from '@/features/balances/BalancesView'
import { BookingsView } from '@/features/bookings/BookingsView'
import { CondoView } from '@/features/condo/CondoView'
import { DeskView } from '@/features/desk/DeskView'
import { OverviewView } from '@/features/overview/OverviewView'
import { ResidentsView } from '@/features/residents/ResidentsView'
import { PermissionsView } from '@/features/staff/PermissionsView'
import { StaffView } from '@/features/staff/StaffView'
import { SectionPlaceholder } from '@/shell/SectionPlaceholder'
import { RouteErrorBoundary } from './RouteErrorBoundary'

/** Screens by section id; sections not listed still render the W0 placeholder. */
const screens: Partial<Record<SectionId, () => ReactElement>> = {
  overview: () => <OverviewView />,
  approvals: () => <ApprovalsView />,
  bookings: () => <BookingsView />,
  balances: () => <BalancesView />,
  desk: () => <DeskView />,
  residents: () => <ResidentsView />,
  condo: () => <CondoView />,
  staff: () => <StaffView />,
}

/** One route per section path, gated by its capability; `/staff/permissions` is a sub-screen of staff. */
const sectionRoutes: RouteObject[] = SECTIONS.flatMap((section) =>
  [section.path, ...(section.extraPaths ?? [])].map((path) => ({
    path: path.slice(1),
    element: (
      <RequireCapability capability={section.capability}>
        {(path === '/staff/permissions' ? <PermissionsView /> : screens[section.id]?.()) ?? (
          <SectionPlaceholder section={section} />
        )}
      </RequireCapability>
    ),
  })),
)

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: '/login', element: <PairingScreen /> },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AccessGate />,
            children: [
              { index: true, element: <ContextEntry /> },
              {
                path: 'c/:condoId',
                element: <CondoRoute />,
                children: [
                  { index: true, element: <FirstSectionRedirect /> },
                  ...sectionRoutes,
                  { path: '*', element: <FirstSectionRedirect /> },
                ],
              },
              { path: '*', element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
]

export const createRouter = () => createBrowserRouter(routes)
export const createTestRouter = (initialEntries: string[]) =>
  createMemoryRouter(routes, { initialEntries })
